import { Link } from "react-router-dom";
import { Flag, Award, Banknote, Car, Star, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import SEO from "@/components/SEO";
import { BUSINESS } from "@/lib/constants";

const TEAM_IMG = "https://images.pexels.com/photos/2698991/pexels-photo-2698991.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1600";

export default function About() {
  return (
    <div data-testid="page-about">
      <SEO
        title="About Carfornia Buyers — Veteran-Owned Car Buyer in Hayward, CA"
        description="Carfornia Buyers is a veteran-owned car-buying business based in Hayward, California, with 25+ years in the business and over 3,000 cars bought. We buy cars, pay same day and stand behind every deal."
        canonical="https://carforniabuyers.com/about"
      />
      <section className="py-16 lg:py-24 bg-brand-surface">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white border border-slate-200 text-xs font-bold uppercase tracking-[0.2em] text-slate-700" data-testid="about-veteran-badge">
            <Flag className="w-3.5 h-3.5 text-brand-orange" /> Veteran-owned &amp; operated
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 tracking-tight mt-5">
            We buy cars. We pay more. We keep our word.
          </h1>
          <p className="text-lg text-slate-600 mt-5 max-w-2xl mx-auto">
            Carfornia Buyers is a veteran-owned car-buying business based in Hayward, California. With more than 25 years in the business and over 3,000 cars bought, we buy cars directly from Bay Area owners and pay the same day.
          </p>
        </div>
      </section>

      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <img src={TEAM_IMG} alt="California open road" className="rounded-3xl w-full object-cover aspect-[5/4] shadow-[0_30px_80px_-20px_rgba(15,23,42,0.2)]" />
          </div>
          <div className="lg:col-span-6 space-y-6">
            <h2 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">How we work.</h2>
            <p className="text-slate-600 leading-relaxed">
              When you submit your car on this site, it comes straight to us — not to a call center or a list of dealers. We review your details, call you back, and arrange a time to see the car in person.
            </p>
            <p className="text-slate-600 leading-relaxed">
              We buy cars to resell them ourselves, not to send them to auction, which is why we can often pay more than the national online buyers. Older, high-mileage, lien, salvage and rebuilt-title cars are all welcome.
            </p>
            <p className="text-slate-600 leading-relaxed">
              After the inspection you get an actual offer in writing. If you accept, we handle the title paperwork with you and pay you the same day. If you don’t, no hard feelings.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 pt-2">
              {[
                { Icon: Car, v: "3,000+", l: "Cars bought" },
                { Icon: Award, v: "25+ years", l: "In the business" },
                { Icon: Banknote, v: "Same day", l: "Payment" },
              ].map((s) => (
                <div key={s.l} className="border-l-2 border-brand-blue pl-4">
                  <s.Icon className="w-5 h-5 text-brand-blue mb-2" />
                  <div className="font-display text-2xl font-extrabold text-slate-900">{s.v}</div>
                  <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mt-1">{s.l}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-20 bg-white" data-testid="yelp-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-3xl border border-slate-100 bg-brand-surface p-8 sm:p-10 grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
            <div className="md:col-span-8">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#D32323]">Real reviews</p>
              <h2 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight mt-2">Rated {BUSINESS.yelpRating} on Yelp.</h2>
              <div className="flex items-center gap-1 mt-3" aria-label={`${BUSINESS.yelpRating} out of 5 stars`}>
                {[0, 1, 2, 3, 4].map((i) => <Star key={i} className="w-5 h-5 text-[#FF1A1A] fill-current" />)}
                <span className="text-sm text-slate-600 ml-2">{BUSINESS.yelpReviews} reviews from Bay Area sellers</span>
              </div>
              <p className="text-slate-600 mt-4 leading-relaxed">We don’t publish testimonials on this site — every review is written by a real seller on Yelp, where we can’t edit them. Read them there, then give us a call.</p>
            </div>
            <div className="md:col-span-4 md:text-right">
              <a href={BUSINESS.yelpUrl} target="_blank" rel="noopener noreferrer" data-testid="about-yelp-cta">
                <Button className="h-12 px-6 bg-[#D32323] hover:bg-[#B71C1C] text-white font-bold rounded-xl">Read our Yelp reviews <ExternalLink className="w-4 h-4 ml-2" /></Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-24 bg-brand-surface">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-blue">Our commitments</p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 tracking-tight mt-2">What you can hold us to.</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { t: "Top dollar, in writing", d: "We work to pay more than the alternatives, and every offer we make is written down after we have inspected the car." },
              { t: "Same-day payment", d: "Once you accept and the paperwork is done, you are paid the same day using the method we agreed on." },
              { t: "Satisfaction guaranteed", d: "Your satisfaction is our priority. If you’re not happy with how we handled your sale, tell us and we’ll make it right." },
            ].map((c) => (
              <div key={c.t} className="bg-white rounded-2xl border border-slate-100 p-7 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
                <h3 className="font-display text-xl font-bold text-slate-900">{c.t}</h3>
                <p className="text-slate-600 mt-2 leading-relaxed text-[15px]">{c.d}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-12 space-y-4">
            <Link to="/sell-your-car">
              <Button data-testid="about-cta" className="h-13 px-8 bg-[#0052FF] hover:bg-[#0040CC] text-white font-bold rounded-xl">Request my offer</Button>
            </Link>
            <p className="text-sm text-slate-500">Or call the owner directly at <a href={BUSINESS.phoneHref} className="text-brand-blue font-semibold">{BUSINESS.phone}</a>.</p>
          </div>
        </div>
      </section>
    </div>
  );
}
